/**
 * Life4Earth V2 — Community MVV (Mission/Vision/Values) API Adapter
 *
 * Endpoint: /api/CommunityMvv
 *
 * Supports multi-language translations via JSONB.
 * Three-tier resolution: DB translation → canonical value → empty.
 */

import { apiFetch } from "./client";

// ── Types ────────────────────────────────────────────────────

export interface ValueItem {
  title: string;
  description: string;
}

/** Per-field translations keyed by language code */
export interface MvvTranslations {
  mission?: Record<string, string>;
  vision?: Record<string, string>;
  values?: Record<string, ValueItem[]>;
}

export interface CommunityMvvResponse {
  id: string;
  communityGuid: string;
  mission: string;
  vision: string;
  values: string;       // JSON array string
  translations: string; // JSON object string
  updatedBy: string | null;
  updatedAt: string;
}

/** Parsed MVV data ready for display */
export interface CommunityMvv {
  id: string;
  communityGuid: string;
  mission: string;
  vision: string;
  values: ValueItem[];
  translations: MvvTranslations;
  updatedAt: string;
}

// ── Helpers ──────────────────────────────────────────────────

function parseResponse(r: CommunityMvvResponse): CommunityMvv {
  let values: ValueItem[] = [];
  try { values = JSON.parse(r.values || "[]"); } catch { /* keep empty */ }

  let translations: MvvTranslations = {};
  try { translations = JSON.parse(r.translations || "{}"); } catch { /* keep empty */ }

  return {
    id: r.id,
    communityGuid: r.communityGuid,
    mission: r.mission,
    vision: r.vision,
    values,
    translations,
    updatedAt: r.updatedAt,
  };
}

/**
 * Resolve a text field for the current language.
 * Three-tier fallback: translation → canonical → empty string.
 */
export function resolveField(
  mvv: CommunityMvv,
  field: 'mission' | 'vision',
  lang: string,
): string {
  const translated = mvv.translations[field]?.[lang];
  if (translated) return translated;
  return mvv[field] || '';
}

/**
 * Resolve values array for the current language.
 */
export function resolveValues(
  mvv: CommunityMvv,
  lang: string,
): ValueItem[] {
  const translated = mvv.translations.values?.[lang];
  if (translated && translated.length > 0) return translated;
  return mvv.values;
}

// ── API Functions ────────────────────────────────────────────

/** Fetch MVV for a community. Returns null if none exists (204). */
export async function fetchCommunityMvv(
  communityGuid: string,
): Promise<CommunityMvv | null> {
  const data = await apiFetch<CommunityMvvResponse | undefined>(
    `/api/CommunityMvv?communityGuid=${encodeURIComponent(communityGuid)}`,
  );
  if (!data) return null;
  return parseResponse(data);
}

/** Upsert (create or update) MVV for a community. */
export async function upsertCommunityMvv(
  communityGuid: string,
  mvv: { mission: string; vision: string; values: ValueItem[]; translations: MvvTranslations },
): Promise<CommunityMvv> {
  const raw = await apiFetch<CommunityMvvResponse>(
    `/api/CommunityMvv?communityGuid=${encodeURIComponent(communityGuid)}`,
    {
      method: "PUT",
      body: JSON.stringify({
        mission: mvv.mission,
        vision: mvv.vision,
        values: JSON.stringify(mvv.values),
        translations: JSON.stringify(mvv.translations),
      }),
    },
  );
  return parseResponse(raw);
}
